import { AnyAction } from "@reduxjs/toolkit";
import { interval } from "rxjs";
import { filter, first, map, mergeMap, pairwise } from "rxjs/operators";
import { Action } from "typescript-fsa";

import { createLoadingEpic } from "@tsp/utils/asyncEntity";
import { Epic, combineEpics, ofAction } from "@tsp/utils/epics";

import { notifierSlice, showNotification } from "@tsp-core/notifier/slice";

import { EnvSegment, selectEnvValues } from "../env";

import { fetchUser, fetchUserPositions, loadingAction, logoutAction } from "./actions";
import { UserRole } from "./enums";
import {
  GetUserPositionsStatusResponse,
  GetUserStatusRequest,
  GetUserStatusResponse,
  UserEpicDependencies,
  UserRoles,
  UserStoreSegment,
} from "./interface";
import { translateUserPositions } from "./translators";

export type State = UserStoreSegment & EnvSegment;

// TODO: Refresh token every 5 minutes
const REFRESH_TOKEN_INTERVAL = 1000 * 60 * 5;

const loadingEpic = createLoadingEpic({
  action: loadingAction,
  selectFetcher: (deps: UserEpicDependencies) => deps.api.getUserStatus,
});

const loadingPositionsEpic = createLoadingEpic({
  action: fetchUserPositions,
  selectFetcher: (deps: UserEpicDependencies) => deps.api.getUserPositions,
});

export const loadingUserStartEpic: Epic<State, {}> = (actions$, state$) =>
  actions$.pipe(
    ofAction(fetchUser.started),
    map((action) => {
      const { DOCFLOW_CLIENT_ORIGIN } = selectEnvValues(state$.value);
      const appUrl = DOCFLOW_CLIENT_ORIGIN;
      return loadingAction.started({ appUrl, ...action.payload });
    }),
  );

export const loadingPositionsStartEpic: Epic<State, {}> = (actions$) =>
  actions$.pipe(
    ofAction(loadingAction.done),
    map(({ payload }) =>
      payload.result.authenticated
        ? fetchUserPositions.started({})
        : fetchUserPositions.done({ result: [], params: {} }),
    ),
  );

const userLoadingDoneEpic: Epic<State, {}> = (actions$) =>
  actions$.pipe(
    ofAction(loadingAction.done, fetchUserPositions.done),
    pairwise(),
    map((actions) => {
      const fetchUserAction = actions.find((action) => loadingAction.done.match(action)) as Action<{
        params: GetUserStatusRequest;
        result: GetUserStatusResponse;
      }>;

      const fetchPositionsResult = actions.find((action) => fetchUserPositions.done.match(action)) as Action<{
        result: GetUserPositionsStatusResponse;
      }>;

      const roles: UserRoles = {};
      Object.entries(fetchUserAction.payload.result.roles).forEach(([key, value]) => {
        roles[key as UserRole] = Boolean(value);
      });

      return fetchUser.done({
        params: fetchUserAction.payload.params,
        result: {
          ...fetchUserAction.payload.result,
          roles,
          positions: translateUserPositions(fetchPositionsResult.payload.result),
        },
      });
    }),
  );

export const updateTokenEpic: Epic<State, UserEpicDependencies> = (actions$, _state$, deps) =>
  actions$.pipe(
    ofAction(loadingAction.done),
    filter((action) => action.payload.result.authenticated),
    map((action) => action.payload.result.token),
    mergeMap((token) => {
      deps.api.setBearerToken(token);
      return [];
    }),
  );

export const initSentryEpic: Epic<State, UserEpicDependencies> = (actions$, state$, deps) =>
  actions$.pipe(
    ofAction(loadingAction.done),
    filter((action) => action.payload.result.authenticated),
    first(),
    mergeMap((action) => {
      const { DOCFLOW_SERVER_ORIGIN: apiServer } = selectEnvValues(state$.value);

      const {
        payload: {
          result: { token, user },
        },
      } = action;

      deps.initSentry({
        token,
        apiServer,
        user: {
          id: user.uuid,
          email: user.email,
          username: user.screenName,
        },
      });

      return [];
    }),
  );

const logoutEpic: Epic<State, UserEpicDependencies> = (actions$, state$, deps) =>
  actions$.pipe(
    ofAction(logoutAction),
    mergeMap(() => {
      deps.api.setBearerToken("");
      window.location.href = state$.value.user.data.logoutUrl;

      return [];
    }),
  );

const autoRefreshTokenEpic: Epic<State, UserEpicDependencies> = (actions$, state$, deps) =>
  actions$.pipe(
    ofAction(loadingAction.done),
    filter((action) => action.payload.result.authenticated),
    mergeMap(() =>
      interval(REFRESH_TOKEN_INTERVAL).pipe(
        mergeMap(() => {
          const { DOCFLOW_CLIENT_ORIGIN } = selectEnvValues(state$.value);
          const appUrl = DOCFLOW_CLIENT_ORIGIN;
          return deps.api.refreshToken({ appUrl }).pipe(mergeMap(() => []));
        }),
      ),
    ),
  );

const errorNotificationEpic: Epic = (actions$) =>
  actions$.pipe(
    ofAction(loadingAction.failed),
    map(
      (action) =>
        showNotification({
          type: "error",
          message: action.payload.error,
        }) as unknown as AnyAction,
    ),
  );

export const userEpic = combineEpics(
  logoutEpic,
  loadingEpic,
  initSentryEpic,
  updateTokenEpic,
  userLoadingDoneEpic,
  autoRefreshTokenEpic,
  loadingPositionsEpic,
  loadingUserStartEpic,
  errorNotificationEpic,
  loadingPositionsStartEpic,
);
